import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Shield,
  Settings,
  Users,
  Map,
  Car,
  DollarSign,
  BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Permission } from "@/types/auth";
import {
  useConfirmDialog,
  createDeleteConfirm,
} from "@/components/ui/confirm-dialog";
import DataTablePagination from "@/components/ui/data-table-pagination";

// Mock data - replace with actual API calls
const mockPermissions: Permission[] = [
  // User permissions
  {
    id: "1",
    name: "Create Users",
    resource: "users",
    action: "create",
    description: "Create new user accounts",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    name: "View Users",
    resource: "users",
    action: "read",
    description: "View user accounts and details",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "3",
    name: "Edit Users",
    resource: "users",
    action: "update",
    description: "Edit user account information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "4",
    name: "Delete Users",
    resource: "users",
    action: "delete",
    description: "Delete user accounts",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "5",
    name: "Manage User Roles",
    resource: "users",
    action: "manage_roles",
    description: "Assign and manage user roles",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },

  // Geography permissions
  {
    id: "6",
    name: "Create Countries",
    resource: "geography",
    action: "create_country",
    description: "Create new countries",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "7",
    name: "View Countries",
    resource: "geography",
    action: "read_country",
    description: "View country information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "8",
    name: "Edit Countries",
    resource: "geography",
    action: "update_country",
    description: "Edit country information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "9",
    name: "Delete Countries",
    resource: "geography",
    action: "delete_country",
    description: "Delete countries",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "10",
    name: "Create States",
    resource: "geography",
    action: "create_state",
    description: "Create new states",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "11",
    name: "View States",
    resource: "geography",
    action: "read_state",
    description: "View state information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "12",
    name: "Edit States",
    resource: "geography",
    action: "update_state",
    description: "Edit state information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "13",
    name: "Delete States",
    resource: "geography",
    action: "delete_state",
    description: "Delete states",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "14",
    name: "Create Cities",
    resource: "geography",
    action: "create_city",
    description: "Create new cities",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "15",
    name: "View Cities",
    resource: "geography",
    action: "read_city",
    description: "View city information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "16",
    name: "Edit Cities",
    resource: "geography",
    action: "update_city",
    description: "Edit city information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "17",
    name: "Delete Cities",
    resource: "geography",
    action: "delete_city",
    description: "Delete cities",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },

  // Booking permissions
  {
    id: "18",
    name: "Create Bookings",
    resource: "bookings",
    action: "create",
    description: "Create new bookings",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "19",
    name: "View Bookings",
    resource: "bookings",
    action: "read",
    description: "View booking information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "20",
    name: "Edit Bookings",
    resource: "bookings",
    action: "update",
    description: "Edit booking information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "21",
    name: "Delete Bookings",
    resource: "bookings",
    action: "delete",
    description: "Delete bookings",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "22",
    name: "Assign Drivers",
    resource: "bookings",
    action: "assign_driver",
    description: "Assign drivers to bookings",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "23",
    name: "Cancel Bookings",
    resource: "bookings",
    action: "cancel",
    description: "Cancel bookings",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },

  // Financial permissions
  {
    id: "24",
    name: "View Fares",
    resource: "financial",
    action: "read_fares",
    description: "View fare information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "25",
    name: "Edit Fares",
    resource: "financial",
    action: "update_fares",
    description: "Edit fare information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "26",
    name: "View Transactions",
    resource: "financial",
    action: "read_transactions",
    description: "View transaction information",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "27",
    name: "Process Refunds",
    resource: "financial",
    action: "process_refunds",
    description: "Process refunds",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },

  // Analytics permissions
  {
    id: "28",
    name: "View Analytics",
    resource: "analytics",
    action: "read_analytics",
    description: "View analytics data",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "29",
    name: "View Reports",
    resource: "analytics",
    action: "read_reports",
    description: "View reports",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "30",
    name: "Export Data",
    resource: "analytics",
    action: "export_data",
    description: "Export data",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },

  // System permissions
  {
    id: "31",
    name: "System Settings",
    resource: "system",
    action: "system_settings",
    description: "Access system settings",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "32",
    name: "View Logs",
    resource: "system",
    action: "view_logs",
    description: "View system logs",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
  {
    id: "33",
    name: "Manage Permissions",
    resource: "system",
    action: "manage_permissions",
    description: "Manage user permissions",
    createdAt: "2024-01-15T10:00:00Z",
    updatedAt: "2024-01-15T10:00:00Z",
  },
];

const PermissionsPage: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>(mockPermissions);
  const [filteredPermissions, setFilteredPermissions] =
    useState<Permission[]>(mockPermissions);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [resourceFilter, setResourceFilter] = useState<string>("all");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] =
    useState<Permission | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: "",
    resource: "",
    action: "",
    description: "",
  });

  // Filter permissions based on search and filters
  useEffect(() => {
    let filtered = permissions;

    if (searchTerm) {
      filtered = filtered.filter(
        (permission) =>
          permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          permission.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          permission.resource
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          permission.action.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (resourceFilter !== "all") {
      filtered = filtered.filter(
        (permission) => permission.resource === resourceFilter
      );
    }

    setFilteredPermissions(filtered);
    setCurrentPage(1);
  }, [permissions, searchTerm, resourceFilter]);

  // Pagination calculations
  const totalItems = filteredPermissions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredPermissions.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getResourceIcon = (resource: string) => {
    switch (resource) {
      case "users":
        return <Users className="h-4 w-4" />;
      case "geography":
        return <Map className="h-4 w-4" />;
      case "bookings":
        return <Car className="h-4 w-4" />;
      case "financial":
        return <DollarSign className="h-4 w-4" />;
      case "analytics":
        return <BarChart3 className="h-4 w-4" />;
      case "system":
        return <Settings className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  const getResourceName = (resource: string) => {
    switch (resource) {
      case "users":
        return "Users";
      case "geography":
        return "Config Management";
      case "bookings":
        return "Bookings";
      case "financial":
        return "Financial";
      case "analytics":
        return "Analytics";
      case "system":
        return "System";
      default:
        return resource;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "create":
        return "default";
      case "read":
        return "secondary";
      case "update":
        return "outline";
      case "delete":
        return "destructive";
      default:
        return "default";
    }
  };

  const handleCreate = () => {
    setSelectedPermission(null);
    setFormData({
      name: "",
      resource: "",
      action: "",
      description: "",
    });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (permission: Permission) => {
    setSelectedPermission(permission);
    setFormData({
      name: permission.name,
      resource: permission.resource,
      action: permission.action,
      description: permission.description,
    });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    if (
      !formData.name ||
      !formData.resource ||
      !formData.action ||
      !formData.description
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (selectedPermission) {
        // Update existing permission
        setPermissions((prev) =>
          prev.map((p) =>
            p.id === selectedPermission.id
              ? {
                  ...p,
                  name: formData.name,
                  resource: formData.resource,
                  action: formData.action,
                  description: formData.description,
                  updatedAt: new Date().toISOString(),
                }
              : p
          )
        );
        toast.success("Permission updated successfully!");
        setIsEditModalOpen(false);
      } else {
        // Create new permission
        const newPermission: Permission = {
          id: Date.now().toString(),
          name: formData.name,
          resource: formData.resource,
          action: formData.action,
          description: formData.description,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setPermissions((prev) => [...prev, newPermission]);
        toast.success("Permission created successfully!");
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      toast.error("Failed to save permission");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (permission: Permission) => {
    confirm(
      createDeleteConfirm(
        "Permission",
        async () => {
          setLoading(true);
          try {
            // Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 500));

            setPermissions((prev) =>
              prev.filter((p) => p.id !== permission.id)
            );
            toast.success("Permission deleted successfully!");
          } catch (error) {
            toast.error("Failed to delete permission");
          } finally {
            setLoading(false);
          }
        },
        `Are you sure you want to delete the permission "${permission.name}"? This will remove it from all roles that currently have this permission.`
      )
    );
  };

  const resources = Array.from(new Set(permissions.map((p) => p.resource)));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Permissions</h1>
          <p className="text-muted-foreground">
            Manage system permissions and access control
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Add Permission
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Permissions
            </CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{permissions.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              User Permissions
            </CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {permissions.filter((p) => p.resource === "users").length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Config Management Permissions
            </CardTitle>
            <Map className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {permissions.filter((p) => p.resource === "geography").length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              System Permissions
            </CardTitle>
            <Settings className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {permissions.filter((p) => p.resource === "system").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search permissions by name, description, or action..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={resourceFilter} onValueChange={setResourceFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by resource" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {resources.map((resource) => (
                  <SelectItem key={resource} value={resource}>
                    {getResourceName(resource)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Permissions ({filteredPermissions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Permission</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((permission) => (
                <TableRow key={permission.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{permission.name}</div>
                        <div className="text-sm text-muted-foreground">
                          ID: {permission.id.slice(0, 8)}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getResourceIcon(permission.resource)}
                      <span>{getResourceName(permission.resource)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getActionColor(permission.action)}>
                      {permission.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground max-w-[300px] truncate">
                      {permission.description}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => handleEdit(permission)}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Edit Permission
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => handleDelete(permission)}
                          className="text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Permission
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredPermissions.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No permissions found</h3>
              <p className="text-muted-foreground">
                {searchTerm || resourceFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Get started by adding your first permission"}
              </p>
            </div>
          ) : (
            <div className="mt-4">
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                className="mt-4"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setIsCreateModalOpen(false);
              setIsEditModalOpen(false);
            }}
          />

          {/* Modal */}
          <div className="relative bg-background border rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold mb-4">
              {selectedPermission ? "Edit Permission" : "Create Permission"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Permission Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Create Users"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Resource</label>
                <Select
                  value={formData.resource}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, resource: value }))
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select resource" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="users">Users</SelectItem>
                    <SelectItem value="geography">Config Management</SelectItem>
                    <SelectItem value="bookings">Bookings</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="analytics">Analytics</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">Action</label>
                <Input
                  value={formData.action}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, action: e.target.value }))
                  }
                  placeholder="e.g., create, read, update, delete"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Brief description of what this permission allows"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(false);
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    {selectedPermission ? "Updating..." : "Creating..."}
                  </>
                ) : selectedPermission ? (
                  "Update Permission"
                ) : (
                  "Create Permission"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default PermissionsPage;
